import React, { useState } from "react";
import { Calendar } from "lucide-react";
import { ReactComponent as PrevMonthIcon } from "../assets/prevMonth.svg";
import { ReactComponent as NextMonthIcon } from "../assets/nextMonth.svg";
import Styles from "./Components.module.scss";

export function DateRangePicker({ dateFrom, dateTo, onDateChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempDateFrom, setTempDateFrom] = useState(dateFrom);
  const [tempDateTo, setTempDateTo] = useState(dateTo);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingFrom, setSelectingFrom] = useState(true);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const formatDateForInput = (year, month, day) => {
    const m = String(month + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${year}-${m}-${d}`;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "Select date";
    const date = new Date(dateStr);
    const month = monthNames[date.getMonth()].substring(0, 3);
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const handleDateClick = (day) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateStr = formatDateForInput(year, month, day);

    if (selectingFrom) {
      setTempDateFrom(dateStr);
      setSelectingFrom(false);
    } else {
      setTempDateTo(dateStr);
      setSelectingFrom(true);
    }
  };

  const handleQuickSelect = (days) => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - days);

    setTempDateFrom(
      formatDateForInput(
        fromDate.getFullYear(),
        fromDate.getMonth(),
        fromDate.getDate()
      )
    );
    setTempDateTo(
      formatDateForInput(today.getFullYear(), today.getMonth(), today.getDate())
    );
  };

  const handleApply = () => {
    onDateChange(tempDateFrom, tempDateTo);
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempDateFrom("");
    setTempDateTo("");
    onDateChange("", "");
  };

  const handleCancel = () => {
    setTempDateFrom(dateFrom);
    setTempDateTo(dateTo);
    setIsOpen(false);
  };

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const isDateInRange = (day) => {
    if (!tempDateFrom || !tempDateTo || !day) return false;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateStr = formatDateForInput(year, month, day);
    return dateStr >= tempDateFrom && dateStr <= tempDateTo;
  };

  const isDateSelected = (day) => {
    if (!day) return false;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateStr = formatDateForInput(year, month, day);
    return dateStr === tempDateFrom || dateStr === tempDateTo;
  };

  const days = getDaysInMonth(currentMonth);

  const CalendarSection = () => (
    <div className={Styles.dateRangeCalendarSection}>
      {/* Month Navigation */}
      <div className={Styles.dateRangeMonthNav}>
        <button onClick={previousMonth} className={Styles.dateRangeMonthButton}>
          <PrevMonthIcon width={20} height={20} />
        </button>
        <p className={Styles.dateRangeMonthLabel}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </p>
        <button onClick={nextMonth} className={Styles.dateRangeMonthButton}>
          <NextMonthIcon width={20} height={20} />
        </button>
      </div>

      {/* Day Names */}
      <div className={Styles.dateRangeDayNames}>
        {dayNames.map((day) => (
          <div key={day} className={Styles.dateRangeDayNameCell}>
            <p className={Styles.dateRangeDayNameText}>{day}</p>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className={Styles.dateRangeGrid}>
        {days.map((day, index) => (
          <div key={index} className={Styles.dateRangeDayWrapper}>
            {day ? (
              <button
                onClick={() => handleDateClick(day)}
                className={`${Styles.dateRangeDayButton} ${
                  isDateSelected(day)
                    ? Styles.dateRangeDaySelected
                    : isDateInRange(day)
                    ? Styles.dateRangeDayInRange
                    : ""
                }`}
              >
                <span className={Styles.dateRangeDayText}>{day}</span>
              </button>
            ) : (
              <div />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const QuickSelectSection = () => {
    const quickSelectOptions = [
      { label: "Last 7 Days", onClick: () => handleQuickSelect(7) },
      { label: "Last 30 Days", onClick: () => handleQuickSelect(30) },
      { label: "Last 3 Months", onClick: () => handleQuickSelect(90) },
      { label: "Last 6 Months", onClick: () => handleQuickSelect(180) },
      {
        label: "This Month",
        onClick: () => {
          const today = new Date();
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          setTempDateFrom(
            formatDateForInput(
              firstDay.getFullYear(),
              firstDay.getMonth(),
              firstDay.getDate()
            )
          );
          setTempDateTo(
            formatDateForInput(
              today.getFullYear(),
              today.getMonth(),
              today.getDate()
            )
          );
        },
      },
      {
        label: "Last Month",
        onClick: () => {
          const today = new Date();
          const firstDay = new Date(
            today.getFullYear(),
            today.getMonth() - 1,
            1
          );
          const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
          setTempDateFrom(
            formatDateForInput(
              firstDay.getFullYear(),
              firstDay.getMonth(),
              firstDay.getDate()
            )
          );
          setTempDateTo(
            formatDateForInput(
              lastDay.getFullYear(),
              lastDay.getMonth(),
              lastDay.getDate()
            )
          );
        },
      },
    ];

    return (
      <div className={Styles.dateRangeQuickSelect}>
        <p className={Styles.dateRangeQuickTitle}>Quick Select</p>
        <div className={Styles.dateRangeQuickList}>
          {quickSelectOptions.map(({ label, onClick }) => (
            <button
              key={label}
              onClick={onClick}
              className={Styles.dateRangeQuickButton}
            >
              <span className={Styles.dateRangeQuickButtonLabel}>{label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const DateRangeDropdown = () => (
    <>
      {/* Backdrop */}
      <div
        className={Styles.dateRangeBackdrop}
        onClick={() => setIsOpen(false)}
      />

      {/* Picker Content */}
      <div className={Styles.dateRangeDropdown}>
        {/* Header */}
        <div className={Styles.dateRangeHeader}>
          <p className={Styles.dateRangeHeaderTitle}>Select Date Range</p>
          <p className={Styles.dateRangeHeaderSubtitle}>
            {selectingFrom ? "Choose start date" : "Choose end date"}
          </p>
        </div>

        <div className={Styles.dateRangeBody}>
          <CalendarSection />
          <QuickSelectSection />
        </div>

        {/* Footer Actions */}
        <div className={Styles.dateRangeFooter}>
          <button
            onClick={handleClear}
            className={Styles.dateRangeFooterButton}
          >
            <span className={Styles.dateRangeFooterButtonLabel}>Clear</span>
          </button>
          <button
            onClick={handleCancel}
            className={Styles.dateRangeFooterButton}
          >
            <span className={Styles.dateRangeFooterButtonLabel}>Cancel</span>
          </button>
          <button
            onClick={handleApply}
            disabled={!tempDateFrom || !tempDateTo}
            className={`${Styles.dateRangePrimaryButton} ${
              !tempDateFrom || !tempDateTo
                ? Styles.dateRangePrimaryButtonDisabled
                : ""
            }`}
          >
            <span className={Styles.dateRangePrimaryButtonLabel}>Apply</span>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className={Styles.dateRangeContainer}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={Styles.dateRangeTrigger}
      >
        <Calendar
          style={{ width: 16, height: 16, color: "#99A1AF" }}
          strokeWidth={1.33}
        />
        <span className={Styles.dateRangeTriggerLabel}>
          {dateFrom && dateTo
            ? `${formatDisplayDate(dateFrom)} - ${formatDisplayDate(dateTo)}`
            : "Select Date Range"}
        </span>
      </button>

      {/* Date Picker Dropdown */}
      {isOpen && <DateRangeDropdown />}
    </div>
  );
}
